/**
 * Beloved — Prayer library data module
 *
 * Broad-Christian, warm, plain-English prayers. No saints, no rosary, no
 * Marian devotions. Contents:
 *  - THEMED_PRAYERS: eight life-themes, each with several original prayers
 *    (each body written to sit between 60 and 120 words).
 *  - BIBLICAL_PRAYERS: prayers drawn straight from Scripture.
 *  - MORNING_PRAYER / EVENING_PRAYER: daily rhythm prayers.
 *
 * Prayer shapes:
 *  - Themed prayer: { title, body }
 *  - Scripture / daily prayer: { id, title, subtitle, icon, author, reference, prayer }
 */

/**
 * Themed personal prayers, keyed by theme id.
 * @type {Record<string, {label: string, icon: string, prayers: Array<{title: string, body: string}>}>}
 */
export const THEMED_PRAYERS = {
  health: {
    label: 'Health & Healing',
    icon: '✚',
    prayers: [
      {
        title: 'A Prayer for Healing',
        body: `Loving God, you know this body of mine and every ache it carries. I bring my need for healing to you now, trusting that you care. Where there is pain, bring relief. Where there is fear about what lies ahead, bring your steady peace. Guide the hands of those who care for me, and give me patience on the slow days. Whether healing comes quickly or gently over time, help me rest in the knowledge that I am held by you. Thank you for never leaving my side. Amen.`,
      },
      {
        title: 'When I Am Unwell',
        body: `Father, being unwell is wearying, and today I feel it deeply. I ask for your comfort in this tiredness and your strength when mine runs low. Ease the worry that circles in my mind, and let me feel that I am not alone in this. Watch over the small hours of the night and the long hours of the day. Give me moments of genuine rest, and lift my spirits with your quiet presence. I trust my body and my future to your loving care, knowing you hold me close. Amen.`,
      },
      {
        title: 'For Someone I Love Who Is Ill',
        body: `Merciful God, I lift up someone I love who is unwell, and I ask you to be near to them. You see what I cannot fix and reach where I cannot go. Surround them with your comfort, ease their pain, and calm their fears. Give wisdom to everyone caring for them, and grant them rest that truly restores. Help me to love them well, with patience and gentle presence. When I feel helpless, remind me that my prayers reach you. Into your kind hands I place them today, and I trust you. Amen.`,
      },
      {
        title: 'Strength for the Body',
        body: `God of life, thank you for the gift of this body, even with its limits and weaknesses. On the days I feel strong, help me be grateful. On the days I feel frail, help me lean on you and on those who love me. Teach me to care for myself with kindness rather than harshness. Give me courage to face appointments, decisions, and uncertainty. Above all, remind me that my worth does not rise or fall with my health. I am yours, cherished and kept, in sickness and in strength alike. Amen.`,
      },
    ],
  },
  family: {
    label: 'Family',
    icon: '⌂',
    prayers: [
      {
        title: 'A Prayer for My Family',
        body: `Loving Father, thank you for the family you have given me, with all its joys and imperfections. Draw us closer to one another and closer to you. Where there is tension, bring understanding. Where there is distance, build bridges of grace. Help us to be patient, quick to forgive, and generous with kindness. Watch over each person by name, in the places I cannot see and the moments I cannot share. Make our home a shelter of love and peace. Bind us together with a bond that hardship cannot break. Amen.`,
      },
      {
        title: 'For My Children and Grandchildren',
        body: `God, I hold my children and grandchildren before you today. You have loved them long before I did, and you love them still. Guard their steps in a world I cannot always protect them from. Give them wisdom in their choices and good friends beside them. When they are far from me, keep them close to you. When they struggle, be their strength; when they succeed, keep them humble and thankful. Help me to love them freely, to pray for them faithfully, and to trust them to your greater care. Amen.`,
      },
      {
        title: 'Peace in the Home',
        body: `Prince of Peace, you know that even a loving home can grow weary and short-tempered. Settle the frayed edges of our life together. Where words have wounded, bring healing. Where old hurts linger, help us to let them go. Teach us to listen before we speak and to forgive as we have been forgiven. Fill our ordinary days with small mercies and gentle patience. May our home be a place where each person feels safe, valued, and loved. Let your peace, not our frustration, rule between us. Thank you for keeping us together. Amen.`,
      },
      {
        title: 'For a Loved One Far Away',
        body: `Faithful God, distance stretches between me and someone I love, and I miss them today. Since I cannot be at their side, I trust them to yours. Go with them through this day, into rooms I will never enter and moments I will never see. Keep them safe, keep them well, and keep them near to you. Ease the ache of separation in my own heart, and turn my longing into prayer. Thank you that no distance is too far for your love to reach. Hold us both until we meet again. Amen.`,
      },
    ],
  },
  grief: {
    label: 'Grief & Loss',
    icon: '☽',
    prayers: [
      {
        title: 'A Prayer in Grief',
        body: `God of all comfort, my heart is heavy with loss, and some days the weight is almost too much. You promised to be near to the brokenhearted, so I come to you just as I am. I do not need my sorrow explained; I need to know I am not alone in it. Sit with me in this ache. Hold the memories that bring both tears and gratitude. Give me strength for the ordinary tasks that grief makes so hard. And when I am ready, lead me gently toward hope. Thank you for staying. Amen.`,
      },
      {
        title: 'Missing Someone I Love',
        body: `Loving God, today the absence of someone I love feels sharp and near. I miss their voice, their presence, the ordinary moments I once took for granted. Thank you for the gift they were to me and for the love that does not end with parting. Comfort me in the empty spaces they left behind. Let me grieve without shame and remember without despair. Hold them safe in your keeping, and hold me too. When the sadness rises like a wave, be my steady ground until it passes. I trust you with them, and with me. Amen.`,
      },
      {
        title: 'When the Days Feel Empty',
        body: `Father, since my loss the days feel long and strangely hollow. Getting up, going on, and facing an unchanged world can feel like too much. Meet me in this emptiness and fill it slowly with your presence. Give me one small reason for each day and one gentle step at a time. Surround me with people who will simply be with me. Remind me that healing is not forgetting, and that moving forward is not leaving love behind. Carry me on the days I cannot carry myself. I am waiting on you, and you are near. Amen.`,
      },
      {
        title: 'Hope Beyond Loss',
        body: `God of hope, in the middle of grief it is hard to believe that light will come again, yet you promise that it will. You wipe away tears and make all things new. Help me trust that this sorrow is not the end of my story. Let me hold both my sadness and my hope in the same hands. Comfort me with the promise that those who rest in you are safe and at peace. Renew my weary heart, little by little, until joy finds its way back. Until then, walk closely with me. I trust your promises. Amen.`,
      },
    ],
  },
  worry: {
    label: 'Worry & Sadness',
    icon: '◇',
    prayers: [
      {
        title: 'A Prayer for Anxious Hearts',
        body: `Loving God, my mind is crowded with worries today, and peace feels far away. You invite me to cast my cares on you, so I lay them down one by one. Take the fears I keep rehearsing and the outcomes I cannot control. Quiet the racing thoughts and slow my anxious heart. Remind me that you hold tomorrow, so I do not have to. Where I have been carrying more than I was meant to, help me let go. Fill the space my worry leaves behind with your surpassing peace. I choose to trust you. Amen.`,
      },
      {
        title: 'When Sadness Weighs Me Down',
        body: `Father, a heaviness has settled over me, and I cannot simply shake it off. You do not turn away from sorrow, so I bring mine honestly to you. I do not have to pretend to be fine in your presence. Meet me in this low place and remind me I am still loved, still held, still yours. Give me strength for today, and hope enough for tomorrow. Send small mercies to lighten the hours. When I have no words, let my quiet turning toward you be prayer enough. Thank you for staying near. Amen.`,
      },
      {
        title: 'Letting Go of What I Cannot Control',
        body: `God, so much of what troubles me lies beyond my reach, yet I keep gripping it tightly. Teach me the freedom of open hands. I release to you the people I cannot change, the outcomes I cannot secure, and the future I cannot see. You are wise where I am uncertain and strong where I am weak. Help me to do what is mine to do and to trust you with the rest. Replace my anxious striving with quiet confidence in your care. You have never lost control, and you will not start now. Amen.`,
      },
      {
        title: 'Peace for a Restless Mind',
        body: `Prince of Peace, my thoughts will not settle, and rest keeps slipping away. Still the noise within me as you once stilled the storm. Where fear whispers worst-case endings, speak your steady truth. Remind me of your faithfulness in every season I have already survived. Draw my attention away from what frightens me and toward you. Breath by breath, help me return to your presence and your promises. Guard my heart and my mind, and let your peace stand watch over me. I am safe in your keeping, now and through whatever comes. Amen.`,
      },
    ],
  },
  gratitude: {
    label: 'Gratitude',
    icon: '✦',
    prayers: [
      {
        title: 'A Grateful Heart',
        body: `Generous God, before I ask for anything today, let me simply say thank you. Thank you for breath and morning light, for the people who love me, and for mercies so ordinary I often overlook them. Open my eyes to the good that surrounds me and slow me down enough to notice it. Where I have been focused on what is missing, turn my gaze to what is present. Gratitude softens my heart and steadies my hope. Make thankfulness my natural response to your endless kindness. For all you are and all you give, I am grateful. Amen.`,
      },
      {
        title: 'Counting My Blessings',
        body: `Father, it is easy to hurry past the gifts you scatter through my days. Today I want to pause and count them. Thank you for shelter and food, for small comforts and quiet joys. Thank you for laughter shared and burdens carried by friends. Thank you for the beauty of the world and for another chance to live in it. Even in hard seasons, your goodness has not failed. Let my thankfulness overflow into kindness toward others. Teach me to live with open, grateful hands rather than clenched, anxious ones. You are so good to me. Amen.`,
      },
      {
        title: 'Thankful in Every Season',
        body: `Faithful God, you have walked with me through bright days and difficult ones alike, and you have never let me go. Thank you for being constant when everything else has changed. In seasons of plenty, keep me humble; in seasons of need, keep me trusting. Help me find something to be grateful for even now, and let that gratitude lift my eyes to you. You are working good I cannot yet see. So I give thanks, not because life is easy, but because you are faithful. My heart rests in your unchanging love. Amen.`,
      },
      {
        title: 'Gratitude for Another Day',
        body: `Lord, thank you for the simple, staggering gift of another day. I did nothing to earn this morning, yet here it is, full of possibility. Help me not to waste it on worry or regret. Let me welcome its hours with gladness and use them well. Thank you for the ordinary graces I will meet today: a kind word, a warm drink, a familiar face, a moment of rest. Teach me to treasure what is right in front of me. However this day unfolds, I receive it as a gift from your hand. Amen.`,
      },
    ],
  },
  sleep: {
    label: 'Rest & Sleep',
    icon: '☾',
    prayers: [
      {
        title: 'An Evening Prayer for Rest',
        body: `Faithful God, the day is done, and I bring its weight to you before I sleep. Thank you for carrying me through these hours. I lay down the things left unfinished and the worries I cannot solve tonight. You do not slumber, so I can. Watch over my home and everyone I love while darkness falls. Quiet my thoughts, ease the tension in my body, and grant me deep and peaceful rest. Let me wake renewed and ready for whatever tomorrow holds. Into your safe keeping I place this night. Thank you for your steady care. Amen.`,
      },
      {
        title: 'When Sleep Will Not Come',
        body: `Lord, the house is quiet, but my mind is not, and rest keeps eluding me. Meet me in these restless hours. Take the thoughts that spin and will not stop, and hold them for me until morning. I do not have to solve everything tonight. Breathe your calm into my tired body and settle my anxious heart. Remind me that you are awake and watching over me, so I am safe to let go. Even if sleep is slow, let your peace be near. I rest in the knowledge that you are holding me. Amen.`,
      },
      {
        title: 'Releasing the Day',
        body: `God, as this day closes, I hand it back to you, the good and the hard alike. Thank you for the moments of grace I noticed and the many I surely missed. Forgive me where I fell short, and free me from carrying regret into the night. I release my tasks, my conversations, and my concerns into your capable hands. You will keep working while I sleep. Let me lay my head down without striving, trusting that I am loved and held. Grant me rest that heals, and a heart at peace. Amen.`,
      },
      {
        title: 'Peace Through the Night',
        body: `Watchful God, thank you for the shelter of the night and the gift of rest. As I lie down, calm every fear that grows louder in the dark. Surround me and those I love with your protection. Let no anxious thought rob me of the sleep my body needs. Fill this quiet with your presence, so that even in stillness I know I am not alone. Keep me through the hours until first light, and let me wake with gratitude and renewed strength. I trust myself to your care until morning comes. Amen.`,
      },
    ],
  },
  guidance: {
    label: 'Guidance',
    icon: '✧',
    prayers: [
      {
        title: 'A Prayer for Direction',
        body: `Wise and loving God, I stand at a crossroads and I am not sure which way to go. You have promised to guide those who ask, so I ask now. Quiet my own noise enough to hear you. Give me clarity where there is confusion and courage to follow once the way is clear. Guard me from choices made in fear or haste. Where I cannot see the whole road, help me trust you for the next step. Light my path, order my decisions, and lead me in the way that is good. I will follow you. Amen.`,
      },
      {
        title: 'When I Must Decide',
        body: `Father, a decision waits for me, and its weight is heavy. I do not want to choose blindly, nor to lean only on my own understanding. Grant me wisdom beyond my own, and peace to confirm the way forward. Show me what I may be overlooking, and free me from the pressure to be perfect. Whatever I choose, remind me that your grace covers my mistakes and your love does not depend on getting it right. Steady my heart, clear my mind, and walk with me into what comes next. I trust your leading. Amen.`,
      },
      {
        title: 'Wisdom for the Day Ahead',
        body: `Lord, before this day fills with its demands, I ask for your wisdom. Help me to know what truly matters and to give my energy to it. Guide my words so they build up rather than tear down. Give me discernment in conversations, patience under pressure, and grace with difficult people. When I am unsure, remind me to pause and ask you. Keep me from rushing past the promptings of your Spirit. Order my hours, and let me walk through this day with a calm and listening heart. Lead me, and I will follow. Amen.`,
      },
      {
        title: 'Trusting God With My Future',
        body: `God, the road ahead is hidden from me, and uncertainty makes me uneasy. Yet you already stand in my tomorrow, preparing the way. Help me to release my need to see the whole path and to trust the One who holds it. Where I am tempted to worry, teach me to pray instead. Give me confidence not in my own plans but in your faithful character. You have led me this far and you will not abandon me now. Whatever the future brings, let me meet it holding your hand. My times are safe with you. Amen.`,
      },
    ],
  },
  thanks: {
    label: 'Thanksgiving',
    icon: '♥',
    prayers: [
      {
        title: 'A Prayer of Thanksgiving',
        body: `Gracious God, I come simply to thank you. Thank you for life, for love, and for your endless patience with me. Thank you for meeting me in both my joys and my struggles and never letting go. You have provided for me in ways I have seen and countless ways I have not. Let my gratitude be more than words; let it shape how I live and how I treat others. Turn my thankfulness into generosity and my blessings into blessing for someone else. For your goodness that follows me all my days, I give you my heartfelt thanks. Amen.`,
      },
      {
        title: 'Thank You for Your Faithfulness',
        body: `Faithful God, looking back, I can see your hand through every season of my life. You were present in the good times I celebrated and the hard times I barely survived. You kept your promises when I doubted and provided when I feared there would not be enough. Thank you for your unfailing love, new every morning and steady through the years. Help me to remember your faithfulness whenever fear returns. You have never failed me yet, and you will not begin now. With a grateful heart, I trust you again today. Amen.`,
      },
      {
        title: 'Grateful for Your Love',
        body: `Loving Father, of all your gifts, your love astounds me most. You loved me before I knew you and you love me still, without conditions and without end. Thank you that nothing I do can make you love me more, and nothing I have done can make you love me less. That love steadies me when I feel unworthy and lifts me when I am low. Help me to rest in it rather than earn it. And let the love I have received overflow to everyone I meet today. Thank you for loving me so completely. Amen.`,
      },
      {
        title: 'Thanks for Small Mercies',
        body: `God, thank you for the small mercies that fill my days and so often go unnoticed. A kind word at the right moment, a problem quietly resolved, strength that arrived just in time, beauty that caught me by surprise. These are your fingerprints on my ordinary life. Forgive me for the times I overlook them or credit them to chance. Open my eyes to your goodness woven through the everyday. Let gratitude become my habit and wonder my response. In the small and the large alike, I see your loving care, and I give thanks. Amen.`,
      },
    ],
  },
};

/**
 * Prayers drawn directly from Scripture.
 * @type {Array<{id: string, title: string, subtitle: string, icon: string, author: string, reference: string, prayer: string}>}
 */
export const BIBLICAL_PRAYERS = [
  {
    id: 'lords_prayer',
    title: "The Lord's Prayer",
    subtitle: 'Taught by Jesus',
    icon: '✝',
    author: 'Jesus Christ',
    reference: 'Matthew 6:9-13',
    prayer: `Our Father, who art in heaven,
Hallowed be thy Name.
Thy Kingdom come,
Thy will be done,
On earth as it is in heaven.

Give us this day our daily bread,
And forgive us our trespasses,
As we forgive those who trespass against us.

And lead us not into temptation,
But deliver us from evil.

For thine is the kingdom,
The power, and the glory,
Forever and ever.

Amen.`,
  },
  {
    id: 'psalm_23',
    title: 'The Lord is My Shepherd',
    subtitle: 'Psalm 23',
    icon: '☧',
    author: 'King David',
    reference: 'Psalm 23',
    prayer: `The Lord is my shepherd; I shall not want.

He makes me lie down in green pastures.
He leads me beside still waters.
He restores my soul.

He leads me in paths of righteousness
for his name's sake.

Even though I walk through the valley
of the shadow of death,
I will fear no evil,
for you are with me;
your rod and your staff,
they comfort me.

You prepare a table before me
in the presence of my enemies;
you anoint my head with oil;
my cup overflows.

Surely goodness and mercy shall follow me
all the days of my life,
and I shall dwell in the house of the Lord
forever.

Amen.`,
  },
  {
    id: 'psalm_51',
    title: 'Prayer of Repentance',
    subtitle: 'Psalm 51',
    icon: '♡',
    author: 'King David',
    reference: 'Psalm 51:1-12',
    prayer: `Have mercy on me, O God,
according to your unfailing love;
according to your great compassion
blot out my transgressions.

Wash away all my iniquity
and cleanse me from my sin.

Create in me a pure heart, O God,
and renew a steadfast spirit within me.

Do not cast me from your presence
or take your Holy Spirit from me.

Restore to me the joy of your salvation
and grant me a willing spirit, to sustain me.

Amen.`,
  },
  {
    id: 'psalm_91',
    title: 'Prayer of Divine Protection',
    subtitle: 'Psalm 91',
    icon: '⛨',
    author: 'Psalm 91',
    reference: 'Psalm 91',
    prayer: `He who dwells in the shelter of the Most High
will rest in the shadow of the Almighty.

I will say of the Lord,
"He is my refuge and my fortress,
my God, in whom I trust."

Surely he will save you from the fowler's snare
and from the deadly pestilence.

He will cover you with his feathers,
and under his wings you will find refuge;
his faithfulness will be your shield and rampart.

You will not fear the terror of night,
nor the arrow that flies by day.

For he will command his angels concerning you
to guard you in all your ways.

Amen.`,
  },
];

/**
 * Morning prayer to begin the day.
 * @type {{id: string, title: string, subtitle: string, icon: string, author: string, prayer: string}}
 */
export const MORNING_PRAYER = {
  id: 'morning_prayer',
  title: 'Morning Prayer',
  subtitle: 'Begin your day with God',
  icon: '✦',
  author: 'Daily Prayer',
  prayer: `Lord, I offer you this day,
all that I think and do and say.

Morning by morning, O Lord, you hear my voice;
morning by morning I lay my requests before you
and wait expectantly.

Grant me, O Lord my God,
a mind to know you,
a heart to seek you,
wisdom to find you,
and a hope of finally embracing you.

I rise today through God's strength to guide me,
God's might to uphold me,
God's wisdom to lead me,
God's hand to guard me.

Be with me, Lord, in all I meet today.

Amen.`,
};

/**
 * Evening prayer to close the day.
 * @type {{id: string, title: string, subtitle: string, icon: string, author: string, prayer: string}}
 */
export const EVENING_PRAYER = {
  id: 'evening_prayer',
  title: 'Evening Prayer',
  subtitle: 'End your day in peace',
  icon: '☽',
  author: 'Daily Prayer',
  prayer: `Lord, as the day comes to an end,
I thank you for all the blessings of this day.

Watch over me, dear Lord, as I rest this night.
Grant me peaceful rest and protection from harm.

I entrust to your mercy all those I love.
Keep us safe in your care through the darkness of night.

Forgive me, Lord, for the wrongs of this day—
for harsh words spoken,
for kindness neglected,
for love withheld.

Into your hands, O Lord, I commend my spirit.

May the Lord grant us a quiet night
and a perfect end.

Amen.`,
};

/**
 * Convenience list of the eight theme ids in display order.
 * @type {ReadonlyArray<string>}
 */
export const THEME_ORDER = [
  'health',
  'family',
  'grief',
  'worry',
  'gratitude',
  'sleep',
  'guidance',
  'thanks',
];

export default THEMED_PRAYERS;
